/**
 * AgentMessage — one assistant turn in the chat thread.
 *
 * Visual contract (locked 2026-06-15):
 *   - Left-aligned with a small amber-tinted "✦" avatar to the left
 *   - Acknowledgment in muted text on its own line (when present)
 *   - Message text in foreground colour, 15px, leading-relaxed
 *   - Max 92% of thread width (room for the avatar on the left)
 *   - Entrance: fade + 6px lift, 240ms cubic-bezier
 *
 * Day 7 (2026-06-15): useReducedMotion gates entrance.
 */

"use client";

import { motion, useReducedMotion } from "framer-motion";

import { MOTION, ms } from "@/lib/design-tokens";

export interface AgentMessageProps {
  /** Short warm acknowledgement word ("Cool", "Got it", "Right"). */
  acknowledgment?: string;
  /** The clarifying question or short status message. */
  message: string;
}

export function AgentMessage({ acknowledgment, message }: AgentMessageProps) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 6 }}
      animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
      transition={{
        duration: ms(reduce ? MOTION.fast : MOTION.smooth),
        ease: [0.16, 1, 0.3, 1],
      }}
      className="flex max-w-[92%] gap-3"
    >
      <div
        className="mt-1 grid h-8 w-8 shrink-0 place-items-center rounded-full"
        style={{
          background: "rgba(244,169,60,0.15)",
          color: "#f4a93c",
        }}
        aria-hidden="true"
      >
        <span style={{ fontSize: "var(--fs-small)", lineHeight: 1 }}>✦</span>
      </div>
      <div className="min-w-0 flex-1">
        {acknowledgment ? (
          <p
            className="text-[13px]"
            style={{ color: "hsl(var(--muted-foreground))", marginBottom: 4 }}
          >
            {acknowledgment}
          </p>
        ) : null}
        <p
          className="text-[15px] leading-relaxed"
          style={{ color: "hsl(var(--foreground))", wordBreak: "break-word" }}
        >
          {message}
        </p>
      </div>
    </motion.div>
  );
}
